import React, { useState, useMemo } from "react";
import { ApiDelete, ApiPost, ApiPut } from "../helpers/API/ApiData";
import { Box, Paper, Typography, TextField, Button, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, List, Toolbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { usePermissions } from "../hooks/usePermissions";
import { usePaginatedApi } from "../hooks/usePaginatedApi";
import { EMAIL_REGEX } from "../utils/validation";
import { sanitizeInput } from "../utils/sanitize";
import { buildContactColumns } from "./contact/contactColumns";
import { useContentLanguage } from "../hooks/useContentLanguage";
import { LanguageTabs } from "../components/LanguageTabs/LanguageTabs";

const Contact = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/contact");
  const [lang, setLang] = useContentLanguage();
  const {
    data: contactData,
    loading: loadingData,
    page,
    count,
    countPerPage,
    handleSearch,
    handlePageChange,
    handleCountPerPageChange,
    refetch,
  } = usePaginatedApi("contact/find");
  const [inputValue, setInputValue] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [isEditApi, setIsEditApi] = useState(false);
  const [idForUserContact, setIdForContactEdit] = useState("");
  const [isContactUsModal, setIsContactUsModal] = useState(false);
  const [show, setShow] = useState(false);
  const [contactIdForDelete, setContactIdForDelete] = useState("");
  const handleOnChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: sanitizeInput(value) });
    setErrors({ ...errors, [name]: "" });
  };

  const handleContactUsModalClose = () => {
    setIsContactUsModal(false);
    setIsEditApi(false);
    setErrors({});
    setInputValue({});
  };

  const handleClose = () => {
    setShow(false);
  };

  const validateforUserData = () => {
    let isFormValid: any = true;
    const errors: any = {};
    if (!inputValue?.contactName?.trim()) {
      isFormValid = false;
      errors["contactName"] = "*Bitte geben Sie den englischen Namen ein";
    }
    if (!inputValue?.contactName_ger?.trim()) {
      isFormValid = false;
      errors["contactName_ger"] = "*Bitte geben Sie den deutschen Namen ein";
    }
    if (
      !inputValue?.email?.trim() ||
      EMAIL_REGEX.test(inputValue?.email) === false
    ) {
      isFormValid = false;
      errors["email"] = "*Bitte geben Sie eine E-Mail ein";
    }
    if (!inputValue?.contactDetails?.trim()) {
      isFormValid = false;
      errors["contactDetails"] = "*Bitte geben Sie die englischen Details ein";
    }
    if (!inputValue?.contactDetails_ger?.trim()) {
      isFormValid = false;
      errors["contactDetails_ger"] = "*Bitte geben Sie die deutschen Details ein";
    }
    if (!inputValue?.telephoneNo?.trim()) {
      isFormValid = false;
      errors["telephoneNo"] = "*Bitte geben Sie die Telefonnummer ein";
    }

    setErrors(errors);
    return isFormValid;
  };

  const addContactdetails = (e: any) => {
    e.preventDefault();
    if (validateforUserData()) {
      setLoading(true);
      const data: any = {
        contactName: inputValue?.contactName,
        contactName_ger: inputValue?.contactName_ger,
        contactDetails: inputValue?.contactDetails,
        contactDetails_ger: inputValue?.contactDetails_ger,
        telephoneNo: inputValue?.telephoneNo,
        email: inputValue?.email,
      };

      ApiPost(`contact/create`, data)
        .then((res: any) => {
          if (res?.status === 200) {
            setIsContactUsModal(false);
            setLoading(false);
            refetch();
            toast.success("Kontakt erfolgreich hinzugefügt");
            setInputValue({});
          } else {
            setLoading(false);
            toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
          }
        })
        .catch((err: any) => {
          setLoading(false);
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
        });
    }
  };

  const editContactDetails = (e: any) => {
    e.preventDefault();
    if (validateforUserData()) {
      setLoading(true);
      const data: any = {
        contactName: inputValue?.contactName,
        contactName_ger: inputValue?.contactName_ger,
        contactDetails: inputValue?.contactDetails,
        contactDetails_ger: inputValue?.contactDetails_ger,
        telephoneNo: inputValue?.telephoneNo,
        email: inputValue?.email,
      };
      ApiPut(`contact/update?id=${idForUserContact}`, data)
        .then(() => {
          setIsEditApi(false);
          setIsContactUsModal(false);
          setLoading(false);
          toast.success("Kontakt erfolgreich aktualisiert");
          setInputValue({});
          refetch();
        })
        .catch((err: any) => {
          setLoading(false);
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
        });
    }
  };

  const deleteThisContact = async () => {
    await ApiDelete(`contact/delete?id=${contactIdForDelete}`)
      .then(() => {
        setShow(false);
        toast.success("Kontakt erfolgreich gelöscht!");
        refetch();
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const contactColumns = useMemo(() => buildContactColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setIsContactUsModal(true);
      setIdForContactEdit(row?._id);
      setInputValue({
        contactName: row?.contactName,
        contactName_ger: row?.contactName_ger,
        contactDetails: row?.contactDetails,
        contactDetails_ger: row?.contactDetails_ger,
        telephoneNo: row?.telephoneNo,
        email: row?.email,
      });
    },
    onDelete: (row: any) => {
      setContactIdForDelete(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Kontaktdaten"
            onSearch={handleSearch}
            onAdd={canCreate ? () => setIsContactUsModal(true) : undefined}
          />
          <LanguageTabs value={lang} onChange={setLang} />
          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              rows={contactData || []}
              columns={contactColumns}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleCountPerPageChange}
            />
          )}
        </Box>
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diesen Kontaktdaten löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => deleteThisContact()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      {isContactUsModal ? (
        <Dialog
          fullScreen
          open={isContactUsModal}
          onClose={handleContactUsModalClose}
        >
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleContactUsModalClose}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
                <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                  <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      Deutsch
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      English
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                  <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                    Name
                  </Typography>
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      id="contactName_ger"
                      name="contactName_ger"
                      value={inputValue?.contactName_ger || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">{errors?.contactName_ger}</Typography>
                  </Box>
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      id="contactName"
                      name="contactName"
                      value={inputValue?.contactName || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">{errors?.contactName}</Typography>
                  </Box>
                </Box>

                {/* E-Mail (shared) */}
                <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                  <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                    E-Mail
                  </Typography>
                  <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      type="email"
                      id="email"
                      name="email"
                      value={inputValue?.email || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">{errors?.email}</Typography>
                  </Box>
                </Box>

                {/* Telefonnummer (shared) */}
                <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                  <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                    Telefonnummer
                  </Typography>
                  <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      type="tel"
                      id="telephoneNo"
                      name="telephoneNo"
                      value={inputValue?.telephoneNo || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">{errors?.telephoneNo}</Typography>
                  </Box>
                </Box>

                {/* Beschreibung (translatable) */}
                <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                  <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                    Beschreibung
                  </Typography>
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      id="contactDetails_ger"
                      name="contactDetails_ger"
                      value={inputValue?.contactDetails_ger || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">
                      {errors?.contactDetails_ger}
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      id="contactDetails"
                      name="contactDetails"
                      value={inputValue?.contactDetails || ""}
                      onChange={handleOnChange}
                    />
                    <Typography variant="caption" color="error">
                      {errors?.contactDetails}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {loading ? (
                    <Button variant="contained" disabled>
                      {isEditApi ? "Speichern" : "Hinzufügen"}
                      <CircularProgress size={20} sx={{ mx: 3, color: "white" }} />
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        isEditApi
                          ? editContactDetails(e)
                          : addContactdetails(e);
                      }}
                    >
                      {isEditApi ? "Speichern" : "Hinzufügen"}
                    </Button>
                  )}
                </Box>
              </Box>
          </List>
        </Dialog>
      ) : null}
    </>
  );
};

export default Contact;
